import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import UserDashboardLayout from '../../components/dashboard/UserDashboardLayout';
import { useAuth } from '../../context/AuthContext';
import api, { getImageUrl } from '../../utils/api';
import { toast } from 'react-toastify';

interface Bookmark {
    _id: string;
    name: string;
    location?: string;
    city?: string;
    address?: string;
    priceRange?: string;
    minPrice?: number;
    maxPrice?: number;
    featuredImage?: string;
    type?: string; // For properties
    propertyType?: string; // For projects
    itemType: 'property' | 'project';
    slug?: string;
}

export default function BookmarkList() {
    const { token } = useAuth();
    const [bookmarks, setBookmarks] = useState<Bookmark[]>([]);
    const [loading, setLoading] = useState(true);

    const fetchWishlist = async () => {
        if (!token) return;
        try {
            setLoading(true);
            const data = await api.getWishlist(token);
            const combined: Bookmark[] = [
                ...(data.wishlist || []).map((p: any) => ({ ...p, itemType: 'property' })),
                ...(data.projectWishlist || []).map((p: any) => ({ ...p, itemType: 'project' }))
            ];
            setBookmarks(combined);
        } catch (error) {
            console.error("Failed to fetch wishlist:", error);
            toast.error("Failed to load wishlist");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchWishlist();
    }, [token]);

    const handleRemove = async (itemId: string, itemType: 'property' | 'project') => {
        if (!token) return;
        try {
            const res = await api.toggleWishlist(token, itemId, itemType);
            if (!res.isSaved) {
                setBookmarks(prev => prev.filter(b => b._id !== itemId));
                toast.info("Removed from wishlist");
            }
        } catch (error) {
            console.error("Failed to remove item:", error);
            toast.error("Failed to remove item");
        }
    };

    const formatPrice = (b: Bookmark) => {
        if (b.priceRange) return b.priceRange;
        if (b.minPrice) return `₹${b.minPrice.toLocaleString()}`;
        return 'Price on Request';
    };

    return (
        <UserDashboardLayout>
            <div className="mb-8">
                <h2 className="text-3xl font-black text-slate-800">Saved Properties</h2>
                <p className="text-blue-950 font-medium">Keep track of the homes and projects you love the most.</p>
            </div>

            <div className="bg-white rounded-[2rem] border border-blue-900/10 shadow-sm overflow-hidden min-h-[400px]">
                <div className="p-8 border-b border-slate-50 flex items-center justify-between bg-white/30">
                    <h4 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                        <i className="fa-solid fa-bookmark text-blue-600"></i>
                        My Wishlist ({bookmarks.length})
                    </h4>
                </div>

                <div className="overflow-x-auto">
                    {loading ? (
                        <div className="py-20 text-center">
                            <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                            <p className="text-blue-950 font-bold uppercase tracking-widest text-xs">Loading wishlist...</p>
                        </div>
                    ) : (
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-white/50">
                                    <th className="px-8 py-5 text-[10px] font-black text-blue-950 uppercase tracking-widest border-b border-blue-900/10">Item Details</th>
                                    <th className="px-8 py-5 text-[10px] font-black text-blue-950 uppercase tracking-widest border-b border-blue-900/10 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {bookmarks.map((item) => (
                                    <tr key={item._id} className="hover:bg-white/50 transition-colors group">
                                        <td className="px-8 py-6">
                                            <div className="flex items-center gap-5">
                                                <div className="w-24 h-20 rounded-2xl overflow-hidden shadow-sm border border-white flex-shrink-0 bg-blue-50">
                                                    <img 
                                                        src={getImageUrl(item.featuredImage)} 
                                                        alt={item.name} 
                                                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" 
                                                    />
                                                </div>
                                                <div className="flex flex-col gap-1">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <span className={`text-[9px] font-black px-2 py-0.5 rounded uppercase tracking-wider ${item.itemType === 'project' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>
                                                            {item.itemType}
                                                        </span>
                                                    </div>
                                                    <h4 className="font-black text-slate-800 hover:text-blue-600 transition-colors">
                                                        <Link to={item.itemType === 'project' ? `/project-details/${item.slug || item._id}` : `/property-details/${item._id}`} className="text-lg">{item.name}</Link>
                                                    </h4>
                                                    <p className="text-xs font-bold text-blue-950 flex items-center gap-1.5">
                                                        <i className="fa-solid fa-location-dot text-rose-500"></i>
                                                        {item.location || item.city || item.address || 'Location N/A'}
                                                    </p>
                                                    <span className="text-sm font-black text-blue-600 mt-1">{formatPrice(item)}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-8 py-6 text-right">
                                            <div className="flex items-center justify-end gap-3">
                                                <Link 
                                                    to={item.itemType === 'project' ? `/project-details/${item.slug || item._id}` : `/property-details/${item._id}`}
                                                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-blue-50 text-blue-600 text-xs font-black hover:bg-blue-100 transition-all"
                                                >
                                                    <i className="fa-solid fa-eye"></i>
                                                    View
                                                </Link>
                                                <button 
                                                    onClick={() => handleRemove(item._id, item.itemType)}
                                                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-rose-50 text-rose-600 text-xs font-black hover:bg-rose-100 transition-all"
                                                >
                                                    <i className="fa-solid fa-trash-can"></i>
                                                    Remove
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {bookmarks.length === 0 && (
                                    <tr>
                                        <td colSpan={2} className="px-8 py-20 text-center">
                                            <div className="max-w-xs mx-auto opacity-30">
                                                <i className="fa-solid fa-folder-open text-6xl text-slate-200 mb-4 font-black"></i>
                                                <p className="font-bold text-blue-950">Your wishlist is currently empty.</p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </UserDashboardLayout>
    );
}



